import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, StatusBadge } from "@sagot/ui";
import { RoutedBy } from "./routed-by";
import type { ConversationDetail } from "./inbox-model";

const GENERIC_TEAM = "our team";
/** Only a plain address becomes a `mailto:` link (no query parts, no whitespace). */
const PLAIN_EMAIL = /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/;

export interface HandoffInfo {
  reason: string | null;
  team: string | null;
  routeSource: string | null;
  /** `workflow:<system key>` when a default workflow placed the handoff (stored apart from `route_source`, which stays how the team was found). */
  routedBy: string | null;
  summary: string | null;
  summarySource: "ai" | "fallback" | null;
  outOfHours: boolean;
  contactEmail: string | null;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);

/** The handoff the server stored on the conversation (`detail.handoff`, free-form JSON). */
export function parseHandoff(raw: ConversationDetail["handoff"]): HandoffInfo | null {
  if (!raw) return null;
  const team = text(raw.team);
  const source = text(raw.summary_source);
  return {
    reason: text(raw.reason),
    team: team && team !== GENERIC_TEAM ? team : null,
    routeSource: text(raw.route_source),
    routedBy: text(raw.routed_by),
    summary: text(raw.summary),
    summarySource: source === "ai" || source === "fallback" ? source : null,
    outOfHours: raw.out_of_hours === true,
    contactEmail: text(raw.contact_email),
  };
}

/**
 * What the team needs before replying: why the chat was handed over, who it was routed to, the
 * stored summary (AI-written, or built from the visitor's own words when the model was down)
 * and the email the visitor left out of hours.
 */
export function HandoffCard({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation();
  const info = parseHandoff(detail.handoff);
  if (!info) return null;
  const { conversation } = detail;
  const target =
    conversation.assignee.type === "user" && conversation.assignee.name
      ? conversation.assignee.name
      : info.routeSource === "fallback" || !info.team
        ? t("engage.inbox.handoff.ownersAndAdmins")
        : t("engage.inbox.handoff.teamQueue", { team: info.team });
  return (
    <section className="rounded-lg border border-line bg-surface p-4" aria-labelledby="handoff-card-title" data-testid="handoff-card">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="headset" className="size-4 text-fg-muted" />
        <h3 id="handoff-card-title" className="text-sm font-bold">
          {info.reason ? t("engage.inbox.handoff.titleReason", { reason: t(`engage.inbox.events.reasons.${info.reason}`, { defaultValue: info.reason }) }) : t("engage.inbox.handoff.title")}
        </h3>
        {conversation.needs_staff ? <StatusBadge status="warning" icon="clock" label={t("engage.inbox.handoff.waiting")} /> : null}
        {info.outOfHours ? <Badge tone="neutral"><Icon name="clock" className="size-3" />{t("engage.inbox.handoff.outOfHours")}</Badge> : null}
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
        <div>
          <dt className="text-fg-subtle">{t("engage.inbox.handoff.routedTo")}</dt>
          <dd className="font-semibold" data-testid="handoff-target">{target}</dd>
        </div>
        {info.routedBy ? <RoutedBy source={info.routedBy} /> : null}
        {info.contactEmail ? (
          <div>
            <dt className="text-fg-subtle">{t("engage.inbox.handoff.visitorEmail")}</dt>
            <dd className="font-semibold">
              {PLAIN_EMAIL.test(info.contactEmail) ? <a className="text-primary-ink underline-offset-2 hover:underline" href={`mailto:${info.contactEmail}`}>{info.contactEmail}</a> : info.contactEmail}
            </dd>
          </div>
        ) : null}
      </dl>
      {info.summary ? (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-fg-subtle">
            <Icon name={info.summarySource === "ai" ? "sparkles" : "file-text"} className="size-3.5" />
            <span className="text-xs font-semibold">{info.summarySource === "ai" ? t("engage.inbox.handoff.aiSummary") : t("engage.inbox.handoff.autoSummary")}</span>
          </div>
          <p className="mt-1 text-sm text-fg" data-testid="handoff-summary">{info.summary}</p>
        </div>
      ) : null}
    </section>
  );
}
